import ErrorBoundary from '@components/ErrorBoundary'
import Loadable from '@components/Loadable'
import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'

import ProtectedRoute from '@components/ProtectedRoute'
import { AuthProvider } from '@context/AuthContext'
import { ConciergeListProvider } from '@context/ConciergeListProvider'
import { SubscriptionProvider } from '@context/SubscriptionProvider'
import MainLayout from '@layouts/MainLayout'
import PreviewLayout from '@layouts/PreviewLayout'
import WebConciergeLayout from '@layouts/WebConciergeLayout'
import WixInstallSuccess from '@pages/WixInstallSuccess'
import { NavigationBlockerProvider } from './CustomRouter'

const pages = {
  Dashboard: () => import('@pages/Dashboard'),
  Customize: () => import('@pages/Customize'),
  Conversations: () => import('@pages/ConversationsPage'),
  ContentPage: () => import('@pages/ContentPage'),
  VenuePage: () => import('@pages/VenuePage'),
  Profile: () => import('@pages/Profile'),
  Settings: () => import('@pages/Settings'),
  AdminDashboard: () => import('@pages/AdminDashboard'),
  ConciergePreview: () => import('@/pages/ConciergePreview'),
  FeedbackPage: () => import('@pages/FeedbackPage'),
}

function createLoadable(page: keyof typeof pages) {
  return Loadable(lazy(pages[page]))
}

const Customize = createLoadable('Customize')
const Conversations = createLoadable('Conversations')
const ContentPage = createLoadable('ContentPage')
const VenuePage = createLoadable('VenuePage')
const Profile = createLoadable('Profile')
const AdminDashboard = createLoadable('AdminDashboard')
const Settings = createLoadable('Settings')
const ConciergePreview = createLoadable('ConciergePreview')
const FeedbackPage = createLoadable('FeedbackPage')

const router = createBrowserRouter([
  {
    path: '/preview/:routedConciergeId',
    element: <PreviewLayout />,
    errorElement: <ErrorBoundary />,
  },

  {
    path: '/',
    element: (
      <AuthProvider>
        <NavigationBlockerProvider>
          <ConciergeListProvider>
            <MainLayout />
          </ConciergeListProvider>
        </NavigationBlockerProvider>
      </AuthProvider>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/wix-install',
        element: <WixInstallSuccess />,
        errorElement: <ErrorBoundary />,
      },
      {
        index: true,
        element: <Navigate to='/web-concierge/preview' replace />,
      },
      {
        path: 'web-concierge',
        element: <WebConciergeLayout />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            element: <ConciergePreview />,
          },
          {
            path: 'customize',
            element: <Customize />,
          },
          {
            path: 'conversations',
            element: <Conversations />,
          },
          {
            path: 'content',
            element: <ContentPage />,
          },
          {
            path: 'venue',
            element: <VenuePage />,
          },
          {
            path: 'preview',
            element: <ConciergePreview />,
          },
        ],
      },
      {
        path: 'settings',
        element: <WebConciergeLayout />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: 'subscription',
            element: (
              <SubscriptionProvider>
                <Settings />
              </SubscriptionProvider>
            ),
          },
          {
            path: 'admin-dashboard',
            element: (
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            ),
          },
          {
            path: 'feedback',
            element: (
              <ProtectedRoute adminOnly={true}>
                <FeedbackPage />
              </ProtectedRoute>
            ),
          },
        ],
      },

      {
        path: 'logout',
        element: <Profile />,
      },
    ],
  },
])

export default router
